'use client'

import { AuthClient } from '@/auth/AuthClient'
import { Button } from '@/components/ui/button'
import { clearUtmFromStorage, getUtmFromStorage } from '@/hooks/useUrlParams'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { AgeValue } from '@/lib/registrationAge'
import { AGE_OPTIONS, isAdult } from '@/lib/registrationAge'
import { PasswordService } from '@/services/password.service'
import { ArrowRight, Check, Circle, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import Link from 'next/link'
import { FormEvent, useMemo, useState } from 'react'

export type RegisterFormSuccessPayload = {
    age: AgeValue
    email: string
    transferData: boolean
}

type RegisterFormProps = {
    className?: string
    fieldIdPrefix?: string
    includeTransferData?: boolean
    isCompact?: boolean
    isInCard?: boolean
    resetOnSuccess?: boolean
    submitLabel?: string
    submittingLabel?: string
    transferDataLabel?: string
    useEmailAsUsername?: boolean
    showInlineErrorMessage?: boolean
    showInlineSuccessMessage?: boolean
    getSuccessMessage?: (payload: RegisterFormSuccessPayload) => string
    onError?: (message: string) => void
    onSuccess?: (payload: RegisterFormSuccessPayload) => void
}

/**
 * Renders the shared registration form used by the public register page
 * and the event-instant conversion dialog.
 */
export function RegisterForm({
    className,
    fieldIdPrefix = 'register',
    includeTransferData = false,
    isCompact = false,
    isInCard = false,
    resetOnSuccess = true,
    submitLabel = "Los geht's!",
    submittingLabel = 'Bitte warten...',
    transferDataLabel = 'Fortschritt übernehmen',
    useEmailAsUsername = false,
    showInlineErrorMessage = false,
    showInlineSuccessMessage = false,
    getSuccessMessage,
    onError,
    onSuccess,
}: RegisterFormProps) {
    const [email, setEmail] = useState('')
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [age, setAge] = useState<AgeValue | ''>('')
    const [parentEmail, setParentEmail] = useState('')
    const [transferData, setTransferData] = useState(false)
    const [acceptedTerms, setAcceptedTerms] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [inlineErrorMessage, setInlineErrorMessage] = useState<string | null>(null)
    const [inlineSuccessMessage, setInlineSuccessMessage] = useState<string | null>(null)

    const showParentEmailField = age !== '' && !isAdult(age)
    const isPasswordInvalid = password.length > 0 && !PasswordService.isValid(password)
    const formClassNames = useMemo(() => {
        const cardClasses = isInCard ? 'glass-card rounded-2xl p-6 shadow-card' : ''
        return [cardClasses, 'space-y-5', className].filter(Boolean).join(' ')
    }, [className, isInCard])

    /**
     * Validates form input and triggers registration with optional event data transfer.
     */
    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        emitError({
            message: '',
            onError,
            setInlineErrorMessage,
            showInlineErrorMessage,
        })
        if (showInlineSuccessMessage) {
            setInlineSuccessMessage(null)
        }

        if (!age) {
            emitError({
                message: 'Bitte wähle dein Alter aus.',
                onError,
                setInlineErrorMessage,
                showInlineErrorMessage,
            })
            return
        }

        const trimmedEmail = email.trim()
        const trimmedUsername = resolveUsername({
            email: trimmedEmail,
            username,
            useEmailAsUsername,
        })

        if (!useEmailAsUsername && trimmedUsername.length < 2) {
            emitError({
                message: 'Bitte gib einen Benutzernamen mit mindestens 2 Zeichen ein.',
                onError,
                setInlineErrorMessage,
                showInlineErrorMessage,
            })
            return
        }

        const trimmedParentEmail = parentEmail.trim()
        if (showParentEmailField && !trimmedParentEmail) {
            emitError({
                message: 'Bitte gib die E-Mail-Adresse deiner Eltern ein.',
                onError,
                setInlineErrorMessage,
                showInlineErrorMessage,
            })
            return
        }

        if (!PasswordService.isValid(password)) {
            emitError({
                message: PasswordService.getValidationMessage(),
                onError,
                setInlineErrorMessage,
                showInlineErrorMessage,
            })
            return
        }

        if (!acceptedTerms) {
            emitError({
                message: 'Bitte bestätige die AGB und Datenschutzerklärung.',
                onError,
                setInlineErrorMessage,
                showInlineErrorMessage,
            })
            return
        }

        setIsSubmitting(true)
        try {
            const utm = getUtmFromStorage()
            const result = await AuthClient.register(
                trimmedEmail,
                password,
                trimmedUsername,
                age,
                trimmedParentEmail || undefined,
                includeTransferData ? transferData : undefined,
                acceptedTerms,
                utm ?? undefined,
            )

            if (!result.ok) {
                emitError({
                    message: result.error ?? 'Registrierung fehlgeschlagen.',
                    onError,
                    setInlineErrorMessage,
                    showInlineErrorMessage,
                })
                return
            }

            clearUtmFromStorage()

            const successPayload: RegisterFormSuccessPayload = {
                age,
                email: trimmedEmail,
                transferData: includeTransferData ? transferData : false,
            }
            onSuccess?.(successPayload)

            if (showInlineSuccessMessage) {
                setInlineSuccessMessage(
                    getSuccessMessage?.(successPayload) ?? buildDefaultSuccessMessage(successPayload),
                )
            }

            if (resetOnSuccess) {
                resetFormFields({
                    setAcceptedTerms,
                    setAge,
                    setEmail,
                    setParentEmail,
                    setPassword,
                    setTransferData,
                    setUsername,
                })
            }
        } catch (_error) {
            emitError({
                message: 'Netzwerkfehler. Bitte versuche es erneut.',
                onError,
                setInlineErrorMessage,
                showInlineErrorMessage,
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className={formClassNames}>
            {useEmailAsUsername ? null : (
                <div className="space-y-2">
                    <Label htmlFor={`${fieldIdPrefix}-username`} className="text-sm font-medium">
                        Benutzername
                    </Label>
                    <Input
                        id={`${fieldIdPrefix}-username`}
                        type="text"
                        placeholder="deinname"
                        value={username}
                        onChange={(inputEvent) => setUsername(inputEvent.target.value)}
                        required
                        className="h-12 rounded-xl bg-background/50"
                    />
                </div>
            )}

            <div className="space-y-2">
                <Label htmlFor={`${fieldIdPrefix}-email`} className="text-sm font-medium">
                    Deine E-Mail
                </Label>
                <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                        id={`${fieldIdPrefix}-email`}
                        type="email"
                        placeholder="deine@email.de"
                        value={email}
                        onChange={(inputEvent) => setEmail(inputEvent.target.value)}
                        required
                        className="h-12 rounded-xl bg-background/50 pl-10"
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor={`${fieldIdPrefix}-password`} className="text-sm font-medium">
                    Passwort
                </Label>
                <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                        id={`${fieldIdPrefix}-password`}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(inputEvent) => setPassword(inputEvent.target.value)}
                        required
                        minLength={PasswordService.MIN_LENGTH}
                        pattern={PasswordService.PATTERN}
                        className={`h-12 rounded-xl bg-background/50 pl-10 pr-10 ${
                            isPasswordInvalid ? 'border-destructive focus-visible:ring-destructive' : ''
                        }`}
                        aria-invalid={isPasswordInvalid}
                    />
                    <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                        aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                </div>
                {password.length > 0 ? (
                    <PasswordChecklist password={password} />
                ) : (
                    <p className="text-xs text-muted-foreground">{PasswordService.getHintText()}</p>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor={`${fieldIdPrefix}-age`} className="text-sm font-medium">
                    Alter
                </Label>
                <select
                    id={`${fieldIdPrefix}-age`}
                    title="Altersauswahl"
                    value={age}
                    onChange={(inputEvent) => setAge(inputEvent.target.value as AgeValue | '')}
                    required
                    className="h-12 w-full rounded-xl border border-input bg-background/50 px-3 text-sm"
                >
                    <option value="">Bitte wähle dein Alter</option>
                    {AGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div
                className="grid transition-all duration-300 ease-in-out"
                style={{ gridTemplateRows: showParentEmailField ? '1fr' : '0fr' }}
            >
                <div className="overflow-hidden">
                    <div className="space-y-5 pt-0">
                        <div className="space-y-2">
                            <Label htmlFor={`${fieldIdPrefix}-parent-email`} className="text-sm font-medium">
                                E-Mail der Eltern
                            </Label>
                            <div className="relative">
                                {/* <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" /> */}
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                <Input
                                    id={`${fieldIdPrefix}-parent-email`}
                                    type="email"
                                    placeholder="eltern@email.de"
                                    // placeholder="Diese E-Mail brauchen wir für die Zustimmung, wenn du unter 16 bist."
                                    value={parentEmail}
                                    onChange={(inputEvent) => setParentEmail(inputEvent.target.value)}
                                    required={showParentEmailField}
                                    className="h-12 rounded-xl bg-background/50 pl-10"
                                />
                            </div>
                            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground wrap-break-word">
                                {/* Unter 16? Dann brauchen wir die Zustimmung eines Elternteils. */}
                                Wenn du unter 16 bist, brauchen wir die Zustimmung eines Elternteils.
                            </p>
                        </div>
                        {/* <div className="flex gap-3 rounded-xl border border-amber-500/15 bg-amber-500/5 p-3"> */}
                        {/* <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" /> */}
                        {/* <div>
                            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground wrap-break-word">
                                Unter 16? Dann brauchen wir die Zustimmung eines Elternteils.
                            </p>
                        </div> */}
                        {/* </div> */}
                    </div>
                </div>
            </div>

            {includeTransferData ? (
                <button
                    type="button"
                    onClick={() => setTransferData(!transferData)}
                    className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all ${
                        transferData
                            ? 'border-primary/30 bg-primary/5'
                            : 'border-primary/10 bg-primary/[0.02] hover:border-primary/20'
                    }`}
                >
                    <div
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-all ${
                            transferData ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                        }`}
                    >
                        {transferData ? (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path
                                    d="M2.5 6L5 8.5L9.5 3.5"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-primary-foreground"
                                />
                            </svg>
                        ) : null}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-1.5">
                            <ArrowRight className="h-4 w-4 text-primary" />
                            <span
                                className={`text-sm font-semibold transition-colors ${transferData ? 'text-foreground' : 'text-muted-foreground'}`}
                            >
                                {transferDataLabel}
                            </span>
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            {/* Dein Chatverlauf und Fortschritt aus dieser Sitzung werden in deinen neuen Account
                            übertragen. */}
                            Dein Chatverlauf wird in deinen neuen Account gespeichert.
                        </p>
                    </div>
                </button>
            ) : null}

            <button
                type="button"
                onClick={() => setAcceptedTerms(!acceptedTerms)}
                className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all ${
                    acceptedTerms
                        ? 'border-primary/30 bg-primary/5'
                        : 'border-primary/10 bg-primary/[0.02] hover:border-primary/20'
                }`}
            >
                <div
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-all ${
                        acceptedTerms ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                    }`}
                >
                    {acceptedTerms ? (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path
                                d="M2.5 6L5 8.5L9.5 3.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-primary-foreground"
                            />
                        </svg>
                    ) : null}
                </div>
                <p className="flex-1 text-xs leading-relaxed text-muted-foreground">
                    Ich habe die{' '}
                    <Link
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                        onClick={(e) => e.stopPropagation()}
                    >
                        AGB
                    </Link>{' '}
                    und{' '}
                    <Link
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                        onClick={(e) => e.stopPropagation()}
                    >
                        Datenschutzerklärung
                    </Link>{' '}
                    gelesen und stimme diesen zu.
                </p>
            </button>

            <Button
                type="submit"
                variant="hero"
                size={isCompact ? 'sm' : 'lg'}
                className="mt-4 w-full"
                disabled={isSubmitting}
            >
                {isSubmitting ? submittingLabel : submitLabel}
            </Button>

            {showParentEmailField ? (
                <p className="text-left text-xs text-muted-foreground">
                    Deine Eltern bekommen eine kurze Bestätigungs-E-Mail.
                </p>
            ) : null}

            {inlineSuccessMessage ? (
                <p className="rounded-xl border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
                    {inlineSuccessMessage}
                </p>
            ) : null}
            {inlineErrorMessage ? (
                <p className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {inlineErrorMessage}
                </p>
            ) : null}
        </form>
    )
}

/**
 * Inline password requirement checklist — replaces the static hint text
 * as soon as the user starts typing.
 */
function PasswordChecklist({ password }: { password: string }) {
    const rules = [
        { label: `Mind. ${PasswordService.MIN_LENGTH} Zeichen`, met: password.length >= PasswordService.MIN_LENGTH },
        { label: 'Enthält einen Buchstaben', met: /[A-Za-z]/.test(password) },
        { label: 'Enthält eine Zahl', met: /\d/.test(password) },
    ]

    const hasSpecialChar = /[^A-Za-z0-9]/.test(password)

    return (
        <ul className="flex flex-col gap-1">
            {rules.map((rule) => (
                <li key={rule.label} className="flex items-center gap-1.5 text-xs">
                    {rule.met ? (
                        <Check className="h-3 w-3 text-emerald-500" />
                    ) : (
                        <Circle className="h-3 w-3 text-muted-foreground/40" />
                    )}
                    <span className={rule.met ? 'text-emerald-500' : 'text-muted-foreground'}>{rule.label}</span>
                </li>
            ))}
            {hasSpecialChar ? (
                <li className="flex items-center gap-1.5 text-xs">
                    <Check className="h-3 w-3 text-emerald-500" />
                    <span className="text-emerald-500">Enthält Sonderzeichen</span>
                </li>
            ) : null}
        </ul>
    )
}

// Resolves username based on the selected form mode.
function resolveUsername(input: { email: string; username: string; useEmailAsUsername: boolean }) {
    if (input.useEmailAsUsername) {
        return input.email
    }

    return input.username.trim()
}

// Clears all local form fields after a successful submission.
function resetFormFields(input: {
    setAcceptedTerms: (value: boolean) => void
    setAge: (value: AgeValue | '') => void
    setEmail: (value: string) => void
    setParentEmail: (value: string) => void
    setPassword: (value: string) => void
    setTransferData: (value: boolean) => void
    setUsername: (value: string) => void
}) {
    input.setEmail('')
    input.setUsername('')
    input.setPassword('')
    input.setAge('')
    input.setParentEmail('')
    input.setTransferData(false)
    input.setAcceptedTerms(false)
}

// Emits errors to callback consumers and optionally renders an inline message.
function emitError(input: {
    message: string
    onError?: (message: string) => void
    setInlineErrorMessage: (message: string | null) => void
    showInlineErrorMessage: boolean
}) {
    input.onError?.(input.message)
    if (!input.showInlineErrorMessage) return
    input.setInlineErrorMessage(input.message || null)
}

// Builds default success copy for adult, minor and transfer registration flows.
function buildDefaultSuccessMessage(payload: RegisterFormSuccessPayload) {
    if (payload.transferData) {
        return 'Account umgestellt! Wir haben dir eine Verifikations-Mail geschickt.'
    }

    return isAdult(payload.age)
        ? 'Account erstellt! Wir haben dir eine Verifikations-Mail geschickt.'
        : 'Wir haben eine Mail an dich und an deine Eltern geschickt. Wenn beide bestätigt haben, dann ist dein Account aktiviert.'
}
